import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { searchCandidateStageRows } from "../../../../Redux/ActionCreators/candidateAction";
import { normalizeStageRow } from "./activityUtils";

// Fetches one page of stage rows for `payload`, refetching whenever the payload changes.
// `refresh()` reloads the current page in place (keeps the rows, sets `refreshing`).
// Nothing is fetched while `enabled` is false (stays in the loading state).
const useStageRows = (jobId, payload, enabled = true) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const requestIdRef = useRef(0);
  const payloadKey = JSON.stringify(payload);

  const load = useCallback(async (silent) => {
    if (!jobId || !enabled) return;
    // Only the latest request may write state, so a slow poll can't overwrite a newer filter.
    const requestId = ++requestIdRef.current;
    if (silent) setRefreshing(true); else setLoading(true);
    const res = await dispatch(searchCandidateStageRows(jobId, JSON.parse(payloadKey)));
    if (requestId !== requestIdRef.current) return;
    // Keep the last good data if a refresh fails.
    if (res?.success || !silent) {
      setRows((res?.rows || []).map(normalizeStageRow));
      setTotal(res?.pagination?.total ?? res?.rows?.length ?? 0);
    }
    setLoading(false);
    setRefreshing(false);
  }, [dispatch, jobId, payloadKey, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    load(false);
    return () => {
      requestIdRef.current += 1; // drop any in-flight response
    };
  }, [load]);

  return { rows, total, loading, refreshing, refresh: () => load(true) };
};

export default useStageRows;
